import { useEffect, useRef, useState } from 'react'
import { LATEST_APPOINTMENT_QUERY } from './graphql'
import type { ActionCenterLatestAppointmentQuery } from 'app/generated/gql/graphql'
import { gqlQuery } from 'app/lib/graphqlClient'
import { ContactJson } from 'app/types/entities'
import { bg } from 'lib/background'
import { logError } from 'lib/logging'

/** One hit of the query below, so the selection can never drift from the type. */
export type LatestAppointment = ActionCenterLatestAppointmentQuery[`appointments`][`hits`][number]

/**
 * The most recent appointment with a given status for this contact.
 *
 * Reads the appointment record itself instead of the contact's
 * `nearestAppointmentDate`. That field is a single denormalized pointer with no
 * status attached, so on a contact with several appointments it can just as
 * easily name an upcoming or completed one as the broken visit the card is
 * talking about. Dev has a contact with six patient records and four broken
 * appointments, which is exactly where a single pointer stops being evidence.
 *
 * Appointments join through `patientIds` (a contact can carry several PMS
 * patient records), and are scoped to the contact's location like everything
 * else on this card.
 */
export function useLatestAppointment(contact?: ContactJson, status?: string): LatestAppointment | undefined {
  const [appointment, setAppointment] = useState<LatestAppointment | undefined>(undefined)
  const generation = useRef(0)
  const patientIds = contact?.patientIds ?? []
  // Primitive dep: the array identity changes on every contact object rebuild.
  const patientKey = patientIds.join(`,`)
  const locationId = contact?.locationId

  useEffect(() => {
    generation.current += 1
    const token = generation.current
    setAppointment(undefined)
    if (!status || !patientKey || !locationId) return
    const list = patientKey
      .split(`,`)
      .map((id) => `"${id}"`)
      .join(` OR `)
    bg(
      gqlQuery<ActionCenterLatestAppointmentQuery>(LATEST_APPOINTMENT_QUERY, {
        search: `patientId:(${list}) AND status:"${status}" AND locationId:"${locationId}"`,
        pagination: { limit: 1 },
        sort: { field: `date`, direction: `DESC` },
      })
        .then((result) => {
          if (token !== generation.current) return
          setAppointment(result.data?.appointments?.hits?.[0])
        })
        .catch((err) => {
          if (token !== generation.current) return
          logError(`Action Center failed to load the ${status} appointment`, err)
        }),
    )
  }, [patientKey, locationId, status])

  return appointment
}
